/**
 * Login page with WorkOS OAuth providers.
 *
 * Blueprint: cxs2/src/components/auth/LoginForm.tsx
 * Simplified for the basic frontend (no Ant Design, uses Tailwind).
 */

import React, { useState, useEffect } from 'react';

const AGENT_BASE = process.env.REACT_APP_AGENT_URL || 'http://localhost:8090';

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  callback_failed: 'Authentication failed. Please try again.',
  session_expired: 'Your session has expired. Please sign in again.',
  access_denied: 'Access denied. Please contact your administrator.',
  unauthorized: 'Unauthorized. Please sign in.',
  no_organization:
    'No organization on your WorkOS account. Join or create an org in CXS2 (FraiOS), then try again.',
  no_redis:
    'Multi-organization sign-in needs Redis. The API server must pass a Redis client to auth routes (orchestrator: set REDIS_URL and restart).',
  token_failed:
    'Could not complete sign-in with CXS2. Set FRAIOS_URL to https://app.fraios.dev (or your running cxs2), ensure TOKEN_SECRET matches the CXS2 JWT secret, and your user and org exist in Convex. Check orchestrator logs for the HTTP status from POST /api/v1/auth/service-token.',
};

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorCode = params.get('error');
    if (errorCode) {
      setError(AUTH_ERROR_MESSAGES[errorCode] || 'Something went wrong. Please try again.');
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const handleSignin = (provider?: string) => {
    setIsLoading(true);
    const params = new URLSearchParams();
    if (provider) {
      params.set('provider', provider);
    }
    if (email.trim()) {
      params.set('email', email.trim());
    }
    params.set('returnTo', '/chat');
    window.location.assign(`${AGENT_BASE}/auth/signin?${params.toString()}`);
  };

  const handleSSO = () => {
    if (!email.trim()) return;
    handleSignin();
  };

  return (
    <div className="h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-full max-w-sm mx-auto p-8 bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Sign in</h1>
          <p className="text-sm text-gray-500 mt-1">CXS Agents</p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Email input */}
        <div className="mb-4">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSSO()}
            disabled={isLoading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
          />
        </div>

        {/* Continue with SSO */}
        <button
          onClick={handleSSO}
          disabled={isLoading || !email.trim()}
          className="w-full py-2 px-4 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
        >
          {isLoading ? 'Redirecting...' : 'Continue with SSO'}
        </button>

        {/* Divider */}
        <div className="relative mb-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        {/* Provider buttons */}
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => handleSignin('google')}
            disabled={isLoading}
            className="py-2 px-3 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Google
          </button>
          <button
            onClick={() => handleSignin('github')}
            disabled={isLoading}
            className="py-2 px-3 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            GitHub
          </button>
          <button
            onClick={() => handleSignin('linkedin')}
            disabled={isLoading}
            className="py-2 px-3 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            LinkedIn
          </button>
        </div>
      </div>
    </div>
  );
}
